import { useEffect, useRef, useState } from 'react';

import { describeError } from '@going-once/client';

/**
 * Re-exported so every screen keeps importing it from here; the implementation moved to
 * `packages/client` when `apps/mobile` needed the same one.
 */
export { describeError } from '@going-once/client';

/**
 * Load something, and be honest about the three states it can be in.
 *
 * The reason this exists rather than a `useState` per screen: an error is a *value* the
 * screen has to render, and the easy version — a try/catch that logs — produces a UI
 * that is permanently loading when something fails. `CapabilityClientError` already
 * carries a human message, a code and a request id, so the screen shows the message and
 * the id rather than "Something went wrong".
 */

export interface AsyncState<T> {
  data?: T;
  error?: { message: string; code?: string; requestId?: string };
  loading: boolean;
  reload: () => void;
}

/**
 * `deps` are **serialized into a key** rather than passed to a `useCallback`, and the
 * loader is held in a ref.
 *
 * The obvious version — `useCallback(load, deps)` — re-runs whenever the caller passes a
 * fresh arrow function, which is every render, so it either loops forever or needs a
 * lint suppression that hides the loop. Keying on the values means the effect re-runs
 * exactly when the inputs change, and the ref means it always calls the current loader
 * instead of one closed over a stale render. `deps` must therefore be JSON-serializable,
 * which for a query's inputs they always are.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [state, setState] = useState<Omit<AsyncState<T>, 'reload'>>({ loading: true });
  const [nonce, setNonce] = useState(0);
  const loader = useRef(load);
  loader.current = load;
  const key = JSON.stringify(deps);

  // `key` and `nonce` are the triggers, not values the body reads — that indirection is
  // the whole design described above, so the rule's advice would break it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the deps are the trigger, by design
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    loader
      .current()
      .then((data) => {
        // A response that arrives after the screen moved on must not write to it.
        if (!cancelled) setState({ data, loading: false });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ error: describeError(error), loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [key, nonce]);

  return { ...state, reload: () => setNonce((value) => value + 1) };
}
